import { computed, ref, unref, type Ref } from 'vue';
import { useRouter } from 'vue-router';
import { storeToRefs } from 'pinia';
import { useToast } from 'vue-toastification';
import { useShipmentFormStore } from '@/stores/useShipmentFormStore';
import { useShipmentFormSave } from '@/composables/useShipmentFormSave';
import {
  useShipmentFormValidation,
  type ShipmentFormGeneralErrors,
} from '@/composables/useShipmentFormValidation';
import type { ShipmentFormStopsPort } from '@/composables/shipment-form-stops-port';
import type { ShipmentEditCapabilities } from '@/components/shipment/edit/utils/shipment-edit-capabilities';
import {
  buildShipmentSavePlanFromChangeContext,
  changeSetFromSavePlan,
  savePlanHasWork,
  type ShipmentFormChangeSet,
} from '@/composables/shipment-form-change-set';
import { editSavePolicyFromCapabilities } from '@/composables/shipment-form-save-policy';
import {
  assertRoutePlanNotStale,
  assertRoutingNotBusy,
  changeSetNeedsStopsValidation,
  validateStopsForSave,
} from '@/composables/shipment-form-save-guards';
import { captureRoutePersistIntent } from '@/components/shipment/form/utils/persist/shipment-route-persist-intent';
import { executeShipmentFormSave } from '@/composables/shipment-form-save-orchestrator';
import type { ShipmentSavePlan } from '@/components/shipment/form/save/shipment-save-plan';

export function useShipmentEditActions(options: {
  accountId: Ref<number>;
  shipmentId: Ref<number>;
  stopsPort: Ref<ShipmentFormStopsPort | null>;
  capabilities: Ref<ShipmentEditCapabilities>;
  routingStrategy: Ref<ShipmentEditCapabilities['routingStrategy']>;
  canEditTracker: Ref<boolean>;
  generalErrors: Ref<ShipmentFormGeneralErrors>;
}) {
  const router = useRouter();
  const toast = useToast();
  const formStore = useShipmentFormStore();
  const {
    stops,
    routePlan,
    generalName,
    generalCarrier,
    generalNotes,
    generalAssetValueCents,
    corridorBufferM,
    geometryQualityPercent,
    selectedDeviceIds,
    selectedDevices,
    persistedStopsPayloadSnapshot,
    serverHydratedRouteFingerprint,
  } = storeToRefs(formStore);

  const saveBusy = ref(false);
  const publishBusy = ref(false);

  const validation = useShipmentFormValidation({
    generalErrors: options.generalErrors,
  });

  const formSave = useShipmentFormSave({
    accountId: options.accountId,
    shipmentId: computed(() => options.shipmentId.value),
    stopsPort: options.stopsPort,
    routingStrategy: options.routingStrategy,
    deviceIds: selectedDeviceIds,
  });

  const savePolicy = computed(() =>
    editSavePolicyFromCapabilities({
      canEditMetadata: options.capabilities.value.canEditMetadata,
      canEditRoute: options.capabilities.value.canEditRoute,
      canEditTracker: options.canEditTracker.value,
    }),
  );

  function buildChangeContext() {
    if (options.capabilities.value.canEditRoute) {
      formStore.applyGeneralPlannedRangeToAnchorStops();
    }

    const selectedByPathId =
      unref(options.stopsPort.value?.selectedByPathId) ?? {};

    return {
      metadataChanged: formStore.haveMetadataChangedSincePersisted(
        generalName.value,
        generalCarrier.value,
        generalNotes.value,
        generalAssetValueCents.value,
      ),
      stops: stops.value,
      stopsChangedSincePersisted: formStore.haveStopsChangedSincePersisted(
        stops.value,
      ),
      persistedStopsPayloadSnapshot: persistedStopsPayloadSnapshot.value,
      deviceIdsChanged: formStore.haveDeviceIdsChangedSincePersisted(
        selectedDeviceIds.value,
      ),
      selectedByPathId,
      routePlan: routePlan.value,
    };
  }

  function buildSavePlan(includePublish: boolean): {
    plan: ShipmentSavePlan;
    changeSet: ShipmentFormChangeSet;
  } {
    const plan = buildShipmentSavePlanFromChangeContext(
      buildChangeContext(),
      savePolicy.value,
      { includePublish },
    );
    return { plan, changeSet: changeSetFromSavePlan(plan) };
  }

  function captureRouteIntent(plan: ShipmentSavePlan) {
    const selectedByPathId =
      unref(options.stopsPort.value?.selectedByPathId) ?? {};
    return captureRoutePersistIntent({
      mode: plan.routes.mode,
      routePlan: routePlan.value,
      selectedByPathId,
      corridorBufferM: corridorBufferM.value,
      geometryQualityPercent: geometryQualityPercent.value,
      stops: stops.value,
    });
  }

  function orchestratorOptions() {
    return {
      accountId: options.accountId,
      shipmentId: options.shipmentId,
      stopsPort: options.stopsPort,
      routingStrategy: options.routingStrategy,
      deviceIds: selectedDeviceIds,
      canEditMetadata: savePolicy.value.canEditMetadata,
      canEditRoute: savePolicy.value.canEditRoute,
      canEditTracker: savePolicy.value.canEditTracker,
      metadataPayload: validation.normalizeMetadataPayload(),
      deviceIdsToAssign: selectedDeviceIds.value,
    };
  }

  async function validateBeforeSave(
    changeSet: ShipmentFormChangeSet,
  ): Promise<boolean> {
    if (
      changeSet.metadata === 'changed' &&
      !validation.validateGeneralFields({ requirePlannedRange: false })
    ) {
      toast.error('Please fix the General section before saving.');
      return false;
    }

    if (changeSetNeedsStopsValidation(changeSet)) {
      if (
        !(await validateStopsForSave({
          validateGeneralFields:
            validation.validateGeneralFields.bind(validation),
          validateStops: () => formSave.validateStops(),
          requirePlannedRange: options.capabilities.value.canEditRoute,
        }))
      ) {
        toast.error('Please fix the Stops section before saving.');
        return false;
      }
      if (!assertRoutingNotBusy(validation, () => formSave.isRoutingBusy())) {
        return false;
      }
      if (
        !assertRoutePlanNotStale(
          changeSet,
          stops.value,
          serverHydratedRouteFingerprint.value,
        )
      ) {
        toast.error('Wait for the route to finish calculating before saving.');
        return false;
      }
    }

    if (changeSet.devices === 'changed') {
      if (
        !validation.validateDeviceSelection(
          selectedDevices.value.map((device) => ({
            value: device.id,
            label: device.label,
          })),
        )
      ) {
        toast.error('Please select a tracker before saving.');
        return false;
      }
    }

    return true;
  }

  async function save(saveOptions?: { redirectAfterSuccess?: boolean }) {
    const redirectAfterSuccess = saveOptions?.redirectAfterSuccess ?? true;
    const { plan, changeSet } = buildSavePlan(false);

    if (!savePlanHasWork(plan)) {
      toast.info('No changes to save.');
      return;
    }

    if (!(await validateBeforeSave(changeSet))) return;

    const routeIntent = captureRouteIntent(plan);

    saveBusy.value = true;
    formStore.clearServerFieldErrors();

    try {
      await executeShipmentFormSave(
        formSave.getPersistDeps(),
        orchestratorOptions(),
        {
          mode: 'edit-save',
          plan,
          changeSet,
          routeIntent,
          suspendHydration: true,
          deferInvalidation: true,
          includePublish: false,
        },
      );

      toast.success('Shipment saved.');
      if (redirectAfterSuccess) {
        await navigateToDetail();
      }
    } catch (err: unknown) {
      validation.handleFormApiError(err, 'Could not save shipment.');
    } finally {
      saveBusy.value = false;
    }
  }

  async function publish() {
    if (!options.capabilities.value.canPublish) return;

    if (
      !validation.validateDeviceSelection(
        selectedDevices.value.map((device) => ({
          value: device.id,
          label: device.label,
        })),
      )
    ) {
      toast.error('Please select a tracker before publishing.');
      return;
    }

    if (
      !assertRoutingNotBusy(
        validation,
        () => formSave.isRoutingBusy(),
        'Wait for the route to finish calculating before publishing.',
      )
    ) {
      return;
    }

    const { plan, changeSet } = buildSavePlan(true);
    if (!(await validateBeforeSave(changeSet))) return;

    const routeIntent = captureRouteIntent(plan);

    publishBusy.value = true;
    formStore.clearServerFieldErrors();

    try {
      await executeShipmentFormSave(
        formSave.getPersistDeps(),
        orchestratorOptions(),
        {
          mode: 'edit-publish',
          plan,
          changeSet,
          routeIntent,
          suspendHydration: true,
          deferInvalidation: true,
          includePublish: true,
        },
      );

      toast.success('Shipment published.');
      await navigateToDetail();
    } catch (err: unknown) {
      validation.handleFormApiError(err, 'Could not publish shipment.');
    } finally {
      publishBusy.value = false;
    }
  }

  function navigateToDetail() {
    return router.push({
      name: 'ShipmentDetailsView',
      params: { id: String(options.shipmentId.value) },
    });
  }

  function cancel() {
    navigateToDetail();
  }

  return {
    saveBusy,
    publishBusy,
    save,
    publish,
    cancel,
  };
}
